import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

export type ErrorKind =
  | "bot_blocked"
  | "recall_down"
  | "offline"
  | "missing_calendar_permissions"
  | "finalize_failed";

const KIND_VISUAL: Record<
  ErrorKind,
  { icon: IconName; tint: string; tintBg: string }
> = {
  bot_blocked: {
    icon: "bot",
    tint: "var(--err)",
    tintBg: "color-mix(in oklab, var(--err) 12%, var(--card))",
  },
  recall_down: {
    icon: "cloud",
    tint: "var(--err)",
    tintBg: "color-mix(in oklab, var(--err) 12%, var(--card))",
  },
  offline: {
    icon: "eyeOff",
    tint: "var(--warn)",
    tintBg: "color-mix(in oklab, var(--warn) 14%, var(--card))",
  },
  missing_calendar_permissions: {
    icon: "cog",
    tint: "var(--warn)",
    tintBg: "color-mix(in oklab, var(--warn) 14%, var(--card))",
  },
  finalize_failed: {
    icon: "close",
    tint: "var(--err)",
    tintBg: "color-mix(in oklab, var(--err) 12%, var(--card))",
  },
};

export type ErrorAction = {
  label: string;
  primary?: boolean;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Optional tooltip when disabled. */
  title?: string;
};

/** Reusable error-screen primitive — used by edge-state routes. The `kind`
 *  drives the icon + tint mapping; the caller supplies the heading +
 *  description + up to 3 actions (the first marked primary if provided).
 *  Centers vertically; caps content width at 520 px.
 */
export function ErrorScreen({
  kind,
  heading,
  description,
  actions,
}: {
  kind: ErrorKind;
  heading: ReactNode;
  description: ReactNode;
  actions?: ReadonlyArray<ErrorAction>;
}) {
  const v = KIND_VISUAL[kind];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 32,
        maxWidth: 520,
        margin: "48px auto",
        textAlign: "center",
      }}
    >
      <div
        data-error-kind={kind}
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: v.tintBg,
          border: `1px solid color-mix(in oklab, ${v.tint} 25%, var(--rule))`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: v.tint,
        }}
      >
        <Icon name={v.icon} size={22} color={v.tint} />
      </div>
      <h2
        className="serif"
        style={{
          margin: 0,
          fontSize: 26,
          fontWeight: 400,
          lineHeight: 1.15,
          color: "var(--ink)",
        }}
      >
        {heading}
      </h2>
      <p
        style={{
          margin: 0,
          fontSize: 14,
          lineHeight: 1.55,
          color: "var(--ink-2)",
        }}
      >
        {description}
      </p>
      {actions && actions.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 8,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {actions.map((a, i) =>
            a.href ? (
              <a
                key={i}
                className={`btn${a.primary ? " primary" : ""}`}
                href={a.href}
                title={a.title}
                style={{
                  textDecoration: "none",
                  pointerEvents: a.disabled ? "none" : undefined,
                  opacity: a.disabled ? 0.55 : 1,
                }}
              >
                {a.label}
              </a>
            ) : (
              <button
                key={i}
                type="button"
                className={`btn${a.primary ? " primary" : ""}`}
                onClick={a.onClick}
                disabled={a.disabled}
                title={a.title}
              >
                {a.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
